import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Calendar, ChevronLeft, ChevronRight, Clock, Eye, Heart, MessageCircle, Bookmark, Share2 } from "lucide-react";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import { ArticleCard } from "@/components/ArticleCard";
import { VideoPlayer } from "@/components/VideoPlayer";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { fetchPost, fetchRelatedPosts, incrementPostView, likePost } from "@/lib/api";
import { toPersianDate, persianNumber, cn } from "@/lib/utils";
import { useBookmarks } from "@/hooks/useBookmarks";
import { branchColor } from "@/lib/mainBranches";
import type { Post } from "@/types";

type SourceLink = { name: string; url: string };

type RelatedImage = { url: string; alt: string };

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const imagesRef = useRef<HTMLDivElement>(null);
  const { isBookmarked, toggle } = useBookmarks();
  const bookmarked = post ? isBookmarked(post.id) : false;

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([fetchPost(slug), fetchRelatedPosts(slug)])
      .then(([p, r]) => {
        setPost(p.data);
        setRelated(r.data);
        incrementPostView(slug).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  const scrollImages = (direction: "prev" | "next") => {
    const el = imagesRef.current;
    if (!el) return;
    const amount = Math.max(240, el.clientWidth * 0.72);
    el.scrollBy({ left: direction === "next" ? -amount : amount, behavior: "smooth" });
  };

  const handleLike = async () => {
    if (!slug || liked) return;
    try {
      const result = await likePost(slug);
      if (result.success && post) {
        setPost({ ...post, likeCount: result.likeCount });
        setLiked(true);
      }
    } catch {}
  };

  const share = async () => {
    if (navigator.share) await navigator.share({ title: post?.title, url: window.location.href });
    else await navigator.clipboard.writeText(window.location.href);
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <Skeleton className="mb-4 h-8 w-3/4" />
        <Skeleton className="mb-8 h-4 w-1/2" />
        <Skeleton className="mb-8 h-64 w-full" />
        <Skeleton className="mb-2 h-4 w-full" />
        <Skeleton className="mb-2 h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-label-primary">مقاله یافت نشد</h1>
        <p className="mt-2 text-label-secondary">مقاله مورد نظر حذف شده یا وجود ندارد.</p>
        <Link to="/" className="mt-6 inline-block text-ios-blue">بازگشت به صفحه اصلی</Link>
      </div>
    );
  }

  const parsedSources: SourceLink[] = post.sources
    ? post.sources.split("\n").filter(s => s.trim()).map(line => {
        const match = line.match(/^(.+?)\s*[:-]\s*(https?:\/\/.+)$/);
        return match ? { name: match[1].trim(), url: match[2].trim() } : null;
      }).filter(Boolean) as SourceLink[]
    : [];

  const ActionButtons = () => (
    <div className="article-actions-inline" role="group" aria-label="کنش‌های مقاله">
      <button type="button" onClick={handleLike} disabled={liked} className={cn("action-box", liked && "is-liked")} aria-pressed={liked} aria-label="پسندیدن">
        <Heart className={cn("h-[18px] w-[18px]", liked && "fill-current")} />
        <span>{persianNumber(post.likeCount)}</span>
      </button>
      <button type="button" onClick={() => toggle(post)} className={cn("action-box", bookmarked && "is-bookmarked")} aria-pressed={bookmarked} aria-label="بوکمارک">
        <Bookmark className={cn("h-[18px] w-[18px]", bookmarked && "fill-current")} />
        <span>ذخیره</span>
      </button>
      {post.telegramDiscussionUrl ? (
        <a href={post.telegramDiscussionUrl} target="_blank" rel="noopener noreferrer" className="action-box" aria-label="بحث در تلگرام">
          <MessageCircle className="h-[18px] w-[18px]" />
          <span>بحث</span>
        </a>
      ) : (
        <span className="action-box opacity-40" aria-disabled="true">
          <MessageCircle className="h-[18px] w-[18px]" />
          <span>بحث</span>
        </span>
      )}
      <button type="button" onClick={share} className="action-box" aria-label="اشتراک‌گذاری">
        <Share2 className="h-[18px] w-[18px]" />
        <span>اشتراک</span>
      </button>
      <span className="action-box" aria-label="تعداد بازدید">
        <Eye className="h-[18px] w-[18px]" />
        <span>{persianNumber(post.viewCount)}</span>
      </span>
    </div>
  );

  const accent = branchColor(post.category?.name, post.category?.accentColor || undefined);
  const relatedImages: RelatedImage[] = [
    post.coverImage ? { url: post.coverImage.url, alt: post.coverImage.alt || post.title } : null,
    post.videoPoster && post.videoPoster !== post.coverImage?.url ? { url: post.videoPoster, alt: `${post.title} - تصویر ویدئو` } : null,
    ...related.map((item) => item.coverImage?.url ? { url: item.coverImage.url, alt: item.coverImage.alt || item.title } : null),
  ].filter(Boolean) as RelatedImage[];
  const videoMarker = "[[video]]";
  const hasVideoMarker = Boolean(post.videoUrl && post.content.includes(videoMarker));
  const [beforeVideo, afterVideo] = hasVideoMarker ? post.content.split(videoMarker) : [post.content, ""];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
      <nav className="mb-4 flex items-center gap-2 text-sm text-label-tertiary">
        <Link to="/" className="hover:text-ios-blue">خانه</Link>
        <ChevronRight className="h-4 w-4 rotate-180" />
        {post.category && (
          <>
            <Link to={`/category/${post.category.slug}`} className="hover:text-ios-blue">{post.category.name}</Link>
            <ChevronRight className="h-4 w-4 rotate-180" />
          </>
        )}
        <span className="text-label-secondary">{post.title}</span>
      </nav>

      {post.coverImage && (
        <div className="article-hero mb-6 overflow-hidden rounded-[28px]">
          <img src={post.coverImage.url} alt={post.title} className="aspect-video w-full object-cover" loading="eager" />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-label-secondary">
        <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{post.publishedAt ? toPersianDate(post.publishedAt) : "—"}</span>
        <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{persianNumber(post.readingTime)} دقیقه مطالعه</span>
        {post.author && <span>{post.author.name}</span>}
        {post.category && (
          <Badge variant="default" className="main-branch-badge" style={{ color: accent, backgroundColor: `${accent}14`, borderColor: `${accent}30`, borderBottom: "none" }}>
            {post.category.name}
          </Badge>
        )}
        {(post.tags ?? []).map((t) => <Link key={t.id} to={`/search?q=${encodeURIComponent(t.name)}`} className="related-chip">{t.name}</Link>)}
      </div>

      <header className="mb-5">
        <h1 className="text-3xl font-black leading-tight text-label-primary sm:text-4xl">{post.title}</h1>
      </header>

      <section className="article-content-card mb-8">
        <article><MarkdownRenderer content={beforeVideo} /></article>

        {relatedImages.length > 0 && (
          <div className="article-related-images-section">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2>تصاویر مرتبط</h2>
              <div className="flex items-center gap-2">
                <span>{persianNumber(relatedImages.length)} تصویر</span>
                <button type="button" className="image-carousel-btn" onClick={() => scrollImages("prev")} aria-label="تصویر قبلی"><ChevronRight className="h-4 w-4" /></button>
                <button type="button" className="image-carousel-btn" onClick={() => scrollImages("next")} aria-label="تصویر بعدی"><ChevronLeft className="h-4 w-4" /></button>
              </div>
            </div>
            <div ref={imagesRef} className="article-related-images-strip">
              {relatedImages.map((img, index) => <figure key={`${img.url}-${index}`}><img src={img.url} alt={img.alt} loading="lazy" /></figure>)}
            </div>
          </div>
        )}

        {post.videoUrl && (
          <div className="article-video-section">
            <div className="mb-3 flex items-center justify-between gap-3"><h2>ویدئوی مرتبط</h2></div>
            <VideoPlayer videoUrl={post.videoUrl} posterUrl={post.videoPoster || post.coverImage?.url} title={post.title} />
          </div>
        )}

        {hasVideoMarker && afterVideo.trim() && <article className="mt-6"><MarkdownRenderer content={afterVideo} /></article>}

        <div className="article-actions-section"><ActionButtons /></div>

        {parsedSources.length > 0 && (
          <div className="article-sources-inline">
            <h2>منابع و پیوندهای مرتبط</h2>
            <div className="flex flex-wrap gap-2">
              {parsedSources.map((source) => <a key={`${source.name}-${source.url}`} href={source.url} target="_blank" rel="noopener noreferrer">{source.name}</a>)}
            </div>
          </div>
        )}
      </section>

      <div className="article-support-addon mb-10">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ios-blue-soft text-ios-blue"><Heart className="h-5 w-5" /></span>
          <div>
            <h3>حمایت مالی از اتمیک</h3>
            <p>اگر این مقاله برایتان مفید بود، با یک حمایت کوچک به ادامه تولید محتوای علمی فارسی کمک کنید.</p>
          </div>
        </div>
        <a href="https://daramet.com/iatomic" target="_blank" rel="noopener noreferrer">حمایت مالی</a>
      </div>

      {related.length > 0 && (
        <section>
          <h2 className="mb-4 text-lg font-bold text-label-primary">مقالات مرتبط</h2>
          <div className="grid gap-6 sm:grid-cols-2">{related.map(p => <ArticleCard key={p.id} post={p} />)}</div>
        </section>
      )}
    </div>
  );
}
